import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import { Button } from "../ui/Button";
import { MultiSelect } from "../FinancialReview/ui";
import FinancialReviewTab from "./FinancialReviewTab";
import CleaningNeededTab from "./CleaningNeededTab";
import AnalysisTab from "./AnalysisTab";
import RetentionTab from "./RetentionTab";
import LtvTab from "./LtvTab";
import { fetchFilters, fetchReport, fetchAnalysis, fetchRetention, fetchLtv } from "./api";

const MONTH_INPUT_CLASS = "flex h-10 w-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm";
const ANALYSIS_DEFAULT_MONTHS = 6;

const utcMonthInput = (date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

const defaultAnalysisEndMonth = () => {
  const now = new Date();
  return utcMonthInput(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));
};

const shiftMonthInput = (yyyyMm, delta) => {
  const [year, month] = yyyyMm.split("-").map(Number);
  return utcMonthInput(new Date(Date.UTC(year, month - 1 + delta, 1)));
};

const DEFAULT_ANALYSIS_END_MONTH = defaultAnalysisEndMonth();
const DEFAULT_ANALYSIS_START_MONTH = shiftMonthInput(DEFAULT_ANALYSIS_END_MONTH, -(ANALYSIS_DEFAULT_MONTHS - 1));
const TABS = ["financialreview", "analysis", "retention", "ltv", "cleaning"];
const DEFAULT_TAB = "financialreview";
const emptyFilters = () => ({
  superOrgs: [],
  specialties: [],
  reportMonth: DEFAULT_ANALYSIS_END_MONTH,
  analysisStartMonth: DEFAULT_ANALYSIS_START_MONTH,
  analysisEndMonth: DEFAULT_ANALYSIS_END_MONTH,
});

export default function CommercialFinancialReview({ setAuth, setUserOrg }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const activeTab = TABS.includes(tabFromUrl) ? tabFromUrl : DEFAULT_TAB;
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === DEFAULT_TAB) next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next);
  };
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [filterOptions, setFilterOptions] = useState({ super_orgs: [], specialties: [] });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const [retentionData, setRetentionData] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionError, setRetentionError] = useState(null);

  const [ltvData, setLtvData] = useState(null);
  const [ltvLoading, setLtvLoading] = useState(false);
  const [ltvError, setLtvError] = useState(null);

  const requestIds = useRef({ filters: 0, report: 0, analysis: 0, retention: 0, ltv: 0 });

  const activeFilters = {
    super_org: applied.superOrgs.length ? applied.superOrgs : undefined,
    specialty: applied.specialties.length ? applied.specialties : undefined,
    report_month: applied.reportMonth || undefined,
  };

  const loadFilters = useCallback(() => {
    const requestId = ++requestIds.current.filters;
    fetchFilters()
      .then((res) => {
        if (requestId !== requestIds.current.filters) return;
        setFilterOptions({ super_orgs: res.super_orgs || [], specialties: res.specialties || [] });
      })
      .catch(() => {});
  }, []);

  const loadReport = useCallback(() => {
    const requestId = ++requestIds.current.report;
    setLoading(true);
    setError(null);
    fetchReport(activeFilters)
      .then((res) => {
        if (requestId !== requestIds.current.report) return;
        setData(res);
      })
      .catch((err) => {
        if (requestId !== requestIds.current.report) return;
        setError(err.message);
      })
      .finally(() => {
        if (requestId !== requestIds.current.report) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const loadAnalysis = useCallback(() => {
    const requestId = ++requestIds.current.analysis;
    setAnalysisLoading(true);
    setAnalysisError(null);
    fetchAnalysis({
      ...activeFilters,
      start_month: applied.analysisStartMonth,
      end_month: applied.analysisEndMonth,
    })
      .then((res) => {
        if (requestId !== requestIds.current.analysis) return;
        setAnalysisData(res);
      })
      .catch((err) => {
        if (requestId !== requestIds.current.analysis) return;
        setAnalysisError(err.message);
      })
      .finally(() => {
        if (requestId !== requestIds.current.analysis) return;
        setAnalysisLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const loadRetention = useCallback(() => {
    const requestId = ++requestIds.current.retention;
    setRetentionLoading(true);
    setRetentionError(null);
    fetchRetention({
      ...activeFilters,
      start_month: applied.analysisStartMonth,
      end_month: applied.analysisEndMonth,
    })
      .then((res) => {
        if (requestId !== requestIds.current.retention) return;
        setRetentionData(res);
      })
      .catch((err) => {
        if (requestId !== requestIds.current.retention) return;
        setRetentionError(err.message);
      })
      .finally(() => {
        if (requestId !== requestIds.current.retention) return;
        setRetentionLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const loadLtv = useCallback(() => {
    const requestId = ++requestIds.current.ltv;
    setLtvLoading(true);
    setLtvError(null);
    fetchLtv({
      super_org: activeFilters.super_org,
      specialty: activeFilters.specialty,
    })
      .then((res) => {
        if (requestId !== requestIds.current.ltv) return;
        setLtvData(res);
      })
      .catch((err) => {
        if (requestId !== requestIds.current.ltv) return;
        setLtvError(err.message);
      })
      .finally(() => {
        if (requestId !== requestIds.current.ltv) return;
        setLtvLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (activeTab === "financialreview" || activeTab === "cleaning") loadReport();
  }, [activeTab, loadReport]);

  useEffect(() => {
    if (activeTab === "analysis") loadAnalysis();
  }, [activeTab, loadAnalysis]);

  useEffect(() => {
    if (activeTab === "retention") loadRetention();
  }, [activeTab, loadRetention]);

  useEffect(() => {
    if (activeTab === "ltv") loadLtv();
  }, [activeTab, loadLtv]);

  const showReportMonth = activeTab === "financialreview" || activeTab === "cleaning";
  const showRangeMonths = activeTab === "analysis" || activeTab === "retention";
  const filtersDirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const filtersActive =
    draft.superOrgs.length ||
    draft.specialties.length ||
    (draft.reportMonth && draft.reportMonth !== DEFAULT_ANALYSIS_END_MONTH) ||
    draft.analysisStartMonth !== DEFAULT_ANALYSIS_START_MONTH ||
    draft.analysisEndMonth !== DEFAULT_ANALYSIS_END_MONTH;

  return (
    <div className="flex">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div className="flex-1 min-w-0 p-6 space-y-6" style={{ marginLeft: "var(--sidebar-width, 250px)" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold">Financial Review</h1>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={activeTab === "financialreview" ? "default" : "outline"}
              onClick={() => setActiveTab("financialreview")}
            >
              Financial Review
            </Button>
            <Button variant={activeTab === "analysis" ? "default" : "outline"} onClick={() => setActiveTab("analysis")}>
              Analysis
            </Button>
            <Button variant={activeTab === "retention" ? "default" : "outline"} onClick={() => setActiveTab("retention")}>
              Retention
            </Button>
            <Button variant={activeTab === "ltv" ? "default" : "outline"} onClick={() => setActiveTab("ltv")}>
              LTV
            </Button>
            <Button
              variant={activeTab === "cleaning" ? "default" : "outline"}
              onClick={() => setActiveTab("cleaning")}
            >
              Cleaning needed
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Super org</label>
            <MultiSelect
              className="w-[220px]"
              values={draft.superOrgs}
              options={filterOptions.super_orgs}
              allLabel="All super orgs"
              onChange={(values) => setDraft((prev) => ({ ...prev, superOrgs: values }))}
              summaryLabel={(count) => `${count} super orgs`}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Specialty</label>
            <MultiSelect
              className="w-[200px]"
              values={draft.specialties}
              options={filterOptions.specialties}
              allLabel="All specialties"
              onChange={(values) => setDraft((prev) => ({ ...prev, specialties: values }))}
              summaryLabel={(count) => `${count} specialties`}
            />
          </div>

          {showReportMonth && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Report month</label>
              <input
                type="month"
                className={MONTH_INPUT_CLASS}
                value={draft.reportMonth}
                onChange={(e) => setDraft((prev) => ({ ...prev, reportMonth: e.target.value }))}
              />
            </div>
          )}

          {showRangeMonths && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Start month</label>
                <input
                  type="month"
                  className={MONTH_INPUT_CLASS}
                  value={draft.analysisStartMonth}
                  max={draft.analysisEndMonth || undefined}
                  onChange={(e) => setDraft((prev) => ({ ...prev, analysisStartMonth: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">End month</label>
                <input
                  type="month"
                  className={MONTH_INPUT_CLASS}
                  value={draft.analysisEndMonth}
                  min={draft.analysisStartMonth || undefined}
                  onChange={(e) => setDraft((prev) => ({ ...prev, analysisEndMonth: e.target.value }))}
                />
              </div>
            </>
          )}

          <Button disabled={!filtersDirty} onClick={() => setApplied({ ...draft })}>
            Submit
          </Button>
          {filtersActive && (
            <Button
              variant="ghost"
              onClick={() => {
                const cleared = emptyFilters();
                setDraft(cleared);
                setApplied(cleared);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        {activeTab === "financialreview" && (
          <FinancialReviewTab
            data={data}
            loading={loading}
            error={error}
            onOpenCleaning={() => setActiveTab("cleaning")}
          />
        )}
        {activeTab === "analysis" && (
          <AnalysisTab data={analysisData} loading={analysisLoading} error={analysisError} />
        )}
        {activeTab === "retention" && (
          <RetentionTab data={retentionData} loading={retentionLoading} error={retentionError} />
        )}
        {activeTab === "ltv" && (
          <LtvTab
            data={ltvData}
            loading={ltvLoading}
            error={ltvError}
            superOrg={activeFilters.super_org}
            specialty={activeFilters.specialty}
          />
        )}
        {activeTab === "cleaning" && (
          <CleaningNeededTab
            data={data}
            loading={loading}
            error={error}
            mappingOptions={filterOptions}
            onLogged={() => {
              loadReport();
              loadFilters();
            }}
          />
        )}
      </div>
    </div>
  );
}
